import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const numberFormatter = new Intl.NumberFormat('en-US')

export function formatCurrency(value) {
  if (value === null || value === undefined || isNaN(value)) return '$0'
  return currencyFormatter.format(value)
}

export function formatCurrencyCompact(value) {
  if (value === null || value === undefined || isNaN(value)) return '$0'
  if (Math.abs(value) >= 1000000) {
    return '$' + (value / 1000000).toFixed(1) + 'M'
  }
  if (Math.abs(value) >= 1000) {
    return '$' + (value / 1000).toFixed(1) + 'K'
  }
  return formatCurrency(value)
}

export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) return '0%'
  return value.toFixed(decimals) + '%'
}

export function formatNumber(value) {
  if (value === null || value === undefined || isNaN(value)) return '0'
  return numberFormatter.format(value)
}

export function parseNumber(value) {
  if (typeof value === 'number') return value
  if (!value) return 0
  const cleaned = String(value).replace(/[$,%\s]/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

function createLocalDate(year, month, day) {
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null
  }
  return date
}

export function parseLocalDate(value) {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime())
  }

  const normalized = String(value).trim()
  let match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (match) {
    return createLocalDate(
      parseInt(match[1], 10),
      parseInt(match[2], 10),
      parseInt(match[3], 10),
    )
  }

  match = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (match) {
    return createLocalDate(
      parseInt(match[3], 10),
      parseInt(match[1], 10),
      parseInt(match[2], 10),
    )
  }

  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatDateOnly(value) {
  const date = parseLocalDate(value)
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function calculateAge(dob, referenceDate = new Date()) {
  if (!dob) return null
  const birthDate = parseLocalDate(dob)
  if (!birthDate) return null

  let age = referenceDate.getFullYear() - birthDate.getFullYear()
  const monthDiff = referenceDate.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
    age--
  }

  return age
}

export function calculateTenure(hireDate, referenceDate = new Date()) {
  if (!hireDate) return null
  const hire = parseLocalDate(hireDate)
  if (!hire) return null

  const diffTime = referenceDate.getTime() - hire.getTime()
  const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25)

  return Math.max(0, Math.round(diffYears * 10) / 10)
}

export function getAgeBand(age) {
  if (age === null || age === undefined) return 'Unknown'
  if (age < 25) return 'Under 25'
  if (age < 35) return '25-34'
  if (age < 45) return '35-44'
  if (age < 55) return '45-54'
  if (age < 65) return '55-64'
  return '65+'
}

export function getCompensationBand(comp) {
  if (comp < 50000) return 'Under $50K'
  if (comp < 75000) return '$50K-$75K'
  if (comp < 100000) return '$75K-$100K'
  if (comp < 150000) return '$100K-$150K'
  if (comp < 200000) return '$150K-$200K'
  return '$200K+'
}

export function generateId() {
  return 'emp_' + Math.random().toString(36).substr(2, 9)
}

// Escape a value for safe use inside a CSS attribute selector (e.g.
// `[data-validation-id="..."]`) when locating an input to focus/scroll to.
export function escapeCssValue(value) {
  if (typeof window !== 'undefined' && window.CSS?.escape) return window.CSS.escape(String(value))
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

// Transiently flash an input target yellow after jumping to it, so the eye
// lands where it was sent – mirroring the Outputs results-row flash. If the
// element sits in the employee census table we flash the whole row; otherwise
// (plan-level fields like eligibility settings) we flash the field itself.
// Removing the class and forcing a reflow before re-adding restarts the CSS
// animation, so a second jump to the same target flashes again.
export function flashInputTarget(element) {
  if (!element) return
  const row = element.closest('.data-table-input tr')
  const target = row || element
  const flashClass = row ? 'input-row-flash' : 'input-field-flash'
  target.classList.remove(flashClass)
  // Force reflow so re-adding the class restarts the animation.
  void target.offsetWidth
  target.classList.add(flashClass)
  window.setTimeout(() => target.classList.remove(flashClass), 1800)
}

export function downloadFile(content, filename, mimeType = 'text/csv') {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
